import classNames from "classnames";
import { memo } from "react";
import { Badge } from "../../../../components/Badge/Badge.tsx";
import { BaseButton } from "../../../../components/Button/BaseButton.tsx";
import { BaseClickableIcon } from "../../../../components/Icon/BaseClickableIcon.tsx";
import { Icon } from "../../../../components/Icon/Icon.tsx";
import { BOARDS, EDIT_WORKSPACE, WORKSPACES } from "../../../../routes.ts";
import { selectedAssetUuidStore } from "../../../boardState/stores/selectedAssetUuidStore.ts";
import { ActionContentTile } from "../../../components/ActionContentTile.tsx";
import { useDebugMode } from "../../../components/DebugMode/hooks/useDebugMode.ts";
import { ContentDownloadButton } from "../../ContentDownload/ContentDownloadButton.tsx";
import { selectedGridAssetsUuidStore } from "../../stores/selectedGridAssetsUuidStore.ts";
import type { Asset, EditTool } from "../../types.ts";

export const MemoAssetTile = memo(
  ({
    asset,
    workspaceUuid,
    boardUuid,
    navigate,
    isDeleteLoading,
    isUserAllowedToCurate,
    addOrRemoveFavoriteTag,
    addOrRemoveFromInspirationLibrary,
    deleteAsset,
    isSelected,
    isSelectionModeOn,
    defaultEditTool,
  }: {
    workspaceUuid: string;
    boardUuid: string;
    asset: Asset;
    navigate: (to: string) => void;
    isDeleteLoading: boolean;
    isUserAllowedToCurate: boolean;
    addOrRemoveFavoriteTag: () => void;
    addOrRemoveFromInspirationLibrary: () => void;
    deleteAsset: ({ uuids }: { uuids: string[] }) => void;
    isSelected: boolean;
    isSelectionModeOn: boolean;
    defaultEditTool: EditTool;
  }) => {
    const isDebugModeOn = useDebugMode();
    // FIXME: update tailwind config to support ring
    return (
      <ActionContentTile
        content={asset.content}
        id={`AssetImageTile-${asset.uuid}`}
        className={isSelected ? "ring-2 ring-[#1a1a1a] rounded-050" : ""}
        contentClassName={isSelected ? "opacity-70" : ""}
        onClick={() => {
          if (isSelectionModeOn) {
            selectedGridAssetsUuidStore.toggleAssetUuidSelection(asset.uuid);
          } else {
            selectedAssetUuidStore.setSelectedAssetUuid(asset.uuid);
            navigate(
              `${WORKSPACES}/${workspaceUuid}/${BOARDS}/${boardUuid}/${EDIT_WORKSPACE}/${defaultEditTool}`,
            );
          }
        }}
        topLeftElement={
          asset.content.status === "SUCCEEDED" && (
            <BaseButton
              className={classNames(
                isSelected ? "" : "invisible group-hover:visible",
                "flex-row items-center pl-100 pr-150 py-100 gap-200 bg-surface-primary-rest rounded-150 shadow-100",
              )}
              onClick={() => {
                selectedGridAssetsUuidStore.toggleAssetUuidSelection(
                  asset.uuid,
                );
              }}
            >
              <div
                className={classNames(
                  "rounded-100 h-400 w-400",
                  isSelected
                    ? "bg-surface-inverse-rest"
                    : "bg-surface-primary-rest border-050",
                )}
              >
                <Icon
                  name="Check"
                  size="sm"
                  className={
                    isSelected ? "stroke-button-primary-rest" : "invisible"
                  }
                />
              </div>
              <div className="label-md-default">
                {isSelected ? "Deselect" : "Select"}
              </div>
            </BaseButton>
          )
        }
        topRightElement={
          <div className="flex-row z-10 gap-100">
            {asset.content.is_from_edit && (
              <Badge size="md" color="transparentDark" label="Edited" />
            )}
            {asset.content.purchased_at && (
              <Badge size="md" color="transparentDark" label="Purchased" />
            )}
          </div>
        }
        bottomRightElement={
          asset.content.status === "SUCCEEDED" &&
          !isSelectionModeOn && (
            <div className="invisible group-hover:visible">
              <div className="flex-row gap-200">
                {isUserAllowedToCurate && isDebugModeOn && (
                  <BaseClickableIcon
                    name="Lightbulb"
                    size="sm"
                    iconClassName={
                      asset.content.is_curated
                        ? "fill-surface-caution-primary-rest stroke-caution-rest"
                        : ""
                    }
                    className="z-10 tile-action"
                    tooltip={{
                      side: "top",
                      content: asset.content.is_curated
                        ? "Remove from inspiration library"
                        : "Add to inspiration library",
                    }}
                    onClick={() => {
                      addOrRemoveFromInspirationLibrary();
                    }}
                  />
                )}
                <BaseClickableIcon
                  name="Heart"
                  size="sm"
                  iconClassName={
                    asset.is_in_favorite
                      ? "fill-error-rest stroke-error-rest"
                      : ""
                  }
                  className="z-10 tile-action"
                  tooltip={{
                    side: "top",
                    content: asset.is_in_favorite
                      ? "Remove from favorites"
                      : "Add to favorites",
                  }}
                  onClick={() => {
                    addOrRemoveFavoriteTag();
                  }}
                />
                {!asset.deleted_at && (
                  <BaseClickableIcon
                    name="Archive"
                    size="sm"
                    className="z-10 tile-action"
                    tooltip={{ side: "top", content: "Archive" }}
                    disabled={isDeleteLoading}
                    onClick={() => {
                      deleteAsset({
                        uuids: [asset.uuid],
                      });
                    }}
                  />
                )}
                <ContentDownloadButton asset={asset} variant="icon" />
                <BaseButton
                  className="z-10 tile-action hover:brightness-95 active:brightness-90 flex-row-center gap-200"
                  onClick={() => {
                    selectedAssetUuidStore.setSelectedAssetUuid(asset.uuid);
                    navigate(
                      `${WORKSPACES}/${workspaceUuid}/${BOARDS}/${boardUuid}/${EDIT_WORKSPACE}/${defaultEditTool}`,
                    );
                  }}
                >
                  <Icon name="PencilLine" size="sm" />
                  Edit
                </BaseButton>
              </div>
            </div>
          )
        }
      />
    );
  },
  (prevProps, nextProps) =>
    prevProps.asset.uuid === nextProps.asset.uuid &&
    prevProps.asset.is_in_favorite === nextProps.asset.is_in_favorite &&
    prevProps.asset.content.is_curated === nextProps.asset.content.is_curated &&
    prevProps.asset.deleted_at === nextProps.asset.deleted_at &&
    prevProps.asset.content.purchased_at ===
      nextProps.asset.content.purchased_at &&
    prevProps.asset.content.status === nextProps.asset.content.status &&
    prevProps.asset.content.url === nextProps.asset.content.url &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isSelectionModeOn === nextProps.isSelectionModeOn &&
    prevProps.defaultEditTool === nextProps.defaultEditTool,
);
MemoAssetTile.displayName = "MemoAssetTile";
